import { LayoutsStream } from "../types";
import store, { LayoutStore } from "../store";
import { ACSStreamData, ACSLayout } from "./messages";
import LayoutImpl from "../layout";
import { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import transformACSLayout from "../transform";

import { Glue42Core } from "@glue42/core";

/**
 * ACS event stream related to layouts
 */
export default class ACSStream implements LayoutsStream {

    public ready: Promise<void>;
    public gotSnapshot: Promise<void>;

    private resolveReady: () => void;
    private rejectReady: (err: any) => void;

    private resolveGotSnapshot: () => void;
    private rejectGotSnapshot: (err: any) => void;

    private readonly StreamName = "T42.ACS.OnLayoutEvent";

    constructor(private agm: Glue42Core.AGM.API, private callbacks: CallbackRegistry) {

        this.ready = new Promise<void>((resolve, reject) => {
            this.resolveReady = resolve;
            this.rejectReady = reject;
        });

        this.gotSnapshot = new Promise<void>((resolve, reject) => {
            this.resolveGotSnapshot = resolve;
            this.rejectGotSnapshot = reject;
        });
    }

    public subscribe(noRetry?: boolean) {
        const transform = (obj: object) => {
            return this.getObjectValues(obj).map((t) => transformACSLayout(t));
        };

        // if we don't have OnLayoutEvent stream  we're running pre 2.1 GD version or outside GD,
        if (!this.checkForLayoutEventMethod()) {
            // if no retry resolve right away
            if (noRetry) {
                this.resolveReady();
            }

            // if we should retry, schedule  one more time
            setTimeout(() => {
                this.subscribe(true);
            }, 500);
        } else {
            this.agm.subscribe(this.StreamName, { waitTimeoutMs: 10000 })
                .then((subs) => {
                    subs.onData((args) => {
                        const data = args.data as ACSStreamData;
                        if (data.IsSnapshot) {
                            this.resolveGotSnapshot();
                        }
                        this.addLayouts(transform(data.OnLayoutAdded), data.IsSnapshot);
                        this.removeLayouts(transform(data.OnLayoutRemoved));
                        this.changeLayouts(transform(data.OnLayoutChanged));
                        this.renameLayouts(transform(data.OnLayoutRenamed));
                        this.restoredLayout(transform(data.OnLayoutRestored));
                        this.callbacks.execute("streamEvent", data);
                    });

                    subs.onFailed((err) => {
                        const msg = `Can not subscribe to "${this.StreamName}" stream - ${JSON.stringify(err)}`;
                        this.rejectReady(msg);
                        this.rejectGotSnapshot(msg);
                    });

                    this.resolveReady();
                })
                .catch((err) => {
                    const msg = `Error subscribing to "${this.StreamName}" stream - ${JSON.stringify(err)}`;
                    this.rejectReady(msg);
                    this.rejectGotSnapshot(msg);
                });
        }
    }

    public onEvent(callback: (streamEvent: ACSStreamData) => void): UnsubscribeFunction {
        return this.callbacks.add("streamEvent", callback);
    }

    public waitFor(token: string, timeout?: number): Promise<void> {
        if (!timeout) {
            timeout = 30000; // default timeout is 30 seconds
        }
        return new Promise<void>((resolve, reject) => {
            let done = false;
            const unsubscribe = this.onEvent((streamEvent) => {
                if (streamEvent.Token === token) {
                    done = true;
                    unsubscribe();
                    resolve();
                }
            });

            setTimeout(() => {
                if (!done) {
                    reject("timed out");
                }
            }, timeout);
        });
    }

    private checkForLayoutEventMethod() {
        try {
            return this.agm
                .methods()
                .map((m) => m.name)
                .indexOf(this.StreamName) !== -1;
        } catch (e) {
            return false;
        }
    }

    private addLayouts(layoutsData: any[], isSnapshot: boolean) {
        if (!layoutsData) {
            return;
        }
        const createAndNotifyLayout = (layoutData: any) => {
            const layout = new LayoutImpl(layoutData);
            store.add(layout);
            this.callbacks.execute("added", layout);
        };

        layoutsData.forEach((layoutData) => {
            if (isSnapshot) {
                const found = store.first((existingLayout) => this.compareLayouts(existingLayout, layoutData));
                if (!found) {
                    createAndNotifyLayout(layoutData);
                }
            } else {
                createAndNotifyLayout(layoutData);
            }
        });
    }

    private removeLayouts(removedLayouts: any[]) {
        if (!removedLayouts) {
            return;
        }
        removedLayouts.forEach((removedLayout) => {
            store.removeWhere((existingLayout) => !this.compareLayouts(existingLayout, removedLayout));

            this.callbacks.execute("removed", removedLayout);
        });
    }

    private changeLayouts(changedLayouts: any[]) {
        if (!changedLayouts) {
            return;
        }

        changedLayouts.forEach((changedLayout) => {
            // removed the changed layout and push it again
            store.removeWhere((existingLayout) => !this.compareLayouts(existingLayout, changedLayout));
            store.add(new LayoutImpl(changedLayout));
            // execute callback
            this.callbacks.execute("changed", changedLayout);
        });

    }

    private renameLayouts(renamedLayouts: any[]) {
        if (!renamedLayouts) {
            return;
        }

        renamedLayouts.forEach((renamedLayout) => {
            const existingLayout = store.first((current) => this.compareLayouts(current, { type: renamedLayout.type, name: renamedLayout.oldName }));

            if (!existingLayout) {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw Error(`received rename event for unknown layout with type ${renamedLayout.type} and name ${renamedLayout.oldName}`);
            }

            existingLayout.name = renamedLayout.newName;

            this.callbacks.execute("renamed", existingLayout);
        });
    }

    private compareLayouts(layout1: { name: string, type: string }, layout2: { name: string, type: string }) {
        return layout1.name === layout2.name && layout1.type === layout2.type;
    }

    private getObjectValues(obj: any) {
        if (!obj) {
            return [];
        }

        return Object.keys(obj).map((k) => obj[k]);
    }

    private restoredLayout(restoredLayouts: any[]) {

        if (!restoredLayouts) {
            return;
        }

        restoredLayouts.forEach((restoredLayout) => {
            const existingLayout = store.first((current) => this.compareLayouts(current, { type: restoredLayout.type, name: restoredLayout.name }));
            this.callbacks.execute("restored", existingLayout);
        });
    }
}
